import type { ReactNode } from 'react';
import { useState } from 'react';
import { Box, Text, Group, Button, SegmentedControl } from '@mantine/core';
import { NAVY } from './styles';

interface YesNoElementProps {
  element: any;
  value: any;
  label: ReactNode;
  onChange: (value: any) => void;
  onBranchSelect: (targetPageId: string, value: any) => void;
}

export function YesNoElement({ element, value, label, onChange, onBranchSelect }: YesNoElementProps) {
  // Remembered here because `value` only changes when the element has a
  // variable bound; the note must show either way.
  const [pickedUnconnected, setPickedUnconnected] = useState(false);
  if (element.createBranches) {
    // Branching YesNo - buttons that navigate to different pages. An option the
    // author has not pointed at a page yet only gets selected; say so rather
    // than leave the viewer clicking a button that seems to do nothing.
    const unconnected = pickedUnconnected;
    return (
      <Box>
        <Text size="sm" fw={500} mb="xs" c="#2A3140">{label}</Text>
        <Group grow>
          <Button
            variant={value === 'yes' ? 'filled' : 'outline'}
            color={NAVY}
            radius="md"
            onClick={() => {
              const targetPageId = element.branches?.yes;
              setPickedUnconnected(!targetPageId);
              if (targetPageId) {
                onBranchSelect(targetPageId, 'yes');
              } else {
                onChange('yes');
              }
            }}
          >
            {element.yesLabel || 'Yes'}
          </Button>
          <Button
            variant={value === 'no' ? 'filled' : 'outline'}
            color={NAVY}
            radius="md"
            onClick={() => {
              const targetPageId = element.branches?.no;
              setPickedUnconnected(!targetPageId);
              if (targetPageId) {
                onBranchSelect(targetPageId, 'no');
              } else {
                onChange('no');
              }
            }}
          >
            {element.noLabel || 'No'}
          </Button>
        </Group>
        {unconnected && (
          <Text size="sm" mt={6} c="orange.8" role="status">
            This option isn't connected to a page yet.
          </Text>
        )}
      </Box>
    );
  }
  // Non-branching YesNo
  return (
    <Box>
      <Text size="sm" fw={500} mb="xs" c="#2A3140">{label}</Text>
      <SegmentedControl
        fullWidth
        data={[
          { label: element.yesLabel || 'Yes', value: 'yes' },
          { label: element.noLabel || 'No', value: 'no' },
        ]}
        value={value || ''}
        onChange={onChange}
        color={NAVY}
        radius="md"
      />
    </Box>
  );
}
